import { Fragment } from 'monteur';
import React from 'react';
import ReactDOM from 'react-dom';
import { App } from './App';
import { convertPerLotSizedQuantitiesToTotal } from './lib/article-utils';
import { sizingDataLocal } from './lib/dummy-data';
import { InitEvent } from './models/events/init-event';
import { SizingData } from './models/sizing-data';
import * as serviceWorker from './serviceWorker';
import { initSentry, captureMessage } from './lib/tracing/sentry';
import { createOrderAndGroups } from './stores/sizing/create-order-and-groups-action';
import { handleError } from './stores/action-mixin';

declare global {
  interface Window {
    RetailPortal: any;
  }
}

async function render(sizingData: SizingData, container: HTMLElement) {
  ReactDOM.render(<App sizingData={sizingData} />, container.querySelector('.presize-root'));
}

function unrender(container: HTMLElement) {
  ReactDOM.unmountComponentAtNode(container.querySelector('.presize-root') as HTMLElement);
}

function loadRetailCenterHeader(): Promise<void> {
  return new Promise((resolve) => {
    const script = window.document.createElement('script');
    script.src = 'https://register.retail-staging.zalando.com/content/staging.js';
    script.addEventListener('load', () => {
      window.RetailPortal.eventEmitter.on('application-ready', resolve);
    });

    const rcWrapper = document.createElement('div');
    rcWrapper.setAttribute('data-retail-portal-app', '');
    rcWrapper.setAttribute('data-retail-portal-auth-protected', '');
    rcWrapper.appendChild(script);
    while (document.body.children.length) {
      rcWrapper.appendChild(document.body.children[0]);
    }

    document.body.appendChild(rcWrapper);
  });
}

(async () => {
  initSentry();
  captureMessage('Sentry initialized');
  if (Fragment.isFragment()) {
    await Fragment.initialize(
      {},
      (eventData: InitEvent, container: Element) => {
        // HYBRID REST/GraphQL Flow (PT-51848)
        // Orders: Always REST (GET/POST /orders)
        // Groups: REST if isDbMigrated(), otherwise GraphQL updateOrder

        // Prepare articles
        const preparedArticles = eventData.sizingData.articles.map((article) => {
          article.sizingNote = null;
          return {
            ...article,
            boxing: convertPerLotSizedQuantitiesToTotal(article.boxing),
            blockOrderBoxing: convertPerLotSizedQuantitiesToTotal(article.blockOrderBoxing),
          };
        });

        // createOrderAndGroups handles the hybrid logic internally:
        // - Orders: Always REST (GET/POST /orders) - migration-ready
        // - Groups: REST if isDbMigrated(), otherwise GraphQL - migration-dependent
        createOrderAndGroups(eventData.sizingData.referenceId, preparedArticles, eventData.sizingData)
          .then((result) => {
            render(
              {
                ...eventData.sizingData,
                articles: preparedArticles,
                groupingCgLevel: result.order.groupingLevel,
                enableBox: result.order.enableBox,
                enableBoxAvailability: true,
                groups: result.groups,
                isNewOrderCreated: result.isNewOrderCreated,
              },
              container as HTMLElement
            );
          })
          .catch((error) => {
            handleError('index.createOrderAndGroups', error, 'Failed to initialize order');
            throw error;
          });
      },
      (container: Element) => {
        unrender(container as HTMLElement);
      }
    );
  } else {
    await loadRetailCenterHeader();
    await render(sizingDataLocal(), document.body);
  }
})();

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
